/**
 * Inventory System Types
 * Based on game_model.yaml inventory section
 */

export type ItemId =
  | 'decompression_chamber'
  | 'hunting_permit'
  | 'diving_fins'
  | 'diving_suits'
  | 'oxygen_tanks'
  | 'capture_nets'
  | 'bio_capsule'
  | 'fuel_canisters'
  | 'empty_fuel_canisters'
  | 'aqua_lily'
  | 'aqua_traps'
  | 'black_box'
  | 'sea_cigars'
  | 'rainbow_eggs'
  | 'emergency_decomp_suit'
  | 'energy_meals'
  | 'environmental_cleanse'
  | 'diving_bell_parts'
  | 'extra_nets_fins'
  | 'fire_flower'
  | 'golden_octopus_captured'
  | 'hunting_log'
  | 'hunting_nets'
  | 'compression_chamber'
  | 'trash_cube'
  | 'photon_locator'
  | 'platinum_journal'
  | 'rainbow_ray_captured'
  | 'star_crabs'
  | 'translation_device';

export interface InventoryItem {
  id: ItemId;
  name: string;
  quantity: number;
}

export type ContainerOption = 'A' | 'B' | 'C' | 'D';

export const ITEM_NAMES: Record<ItemId, string> = {
  decompression_chamber: 'Inflatable Decompression Chamber',
  hunting_permit: '6-day Hunting Permit',
  diving_fins: 'Pairs of diving fins and gloves',
  diving_suits: 'Sets of diving suit and facemask',
  oxygen_tanks: 'Refillable oxygen tanks',
  capture_nets: 'Aquatic capture nets (ACTs)',
  bio_capsule: 'Bio transport capsule',
  fuel_canisters: 'Nuclear fuel canisters for the diving bell',
  empty_fuel_canisters: '(Empty) nuclear fuel canisters',
  aqua_lily: 'Aqua Lily',
  aqua_traps: 'Aqua Traps',
  black_box: 'Black Box',
  sea_cigars: 'Box of Sea Cigars',
  rainbow_eggs: 'Rainbow Ray Eggs',
  emergency_decomp_suit: 'Emergency decomp suit',
  energy_meals: 'Emergency energy meals',
  environmental_cleanse: 'Environmental Cleanse',
  diving_bell_parts: 'Extra Diving Bell parts',
  extra_nets_fins: 'Extra nets and fins',
  fire_flower: 'Fire Flower',
  golden_octopus_captured: 'Golden Octopus inside of a Bio Transport Capsule',
  hunting_log: 'Hunting Log',
  hunting_nets: 'Hunting nets',
  compression_chamber: 'Inflatable Compression Chamber',
  trash_cube: 'Compact Cube of Trash',
  photon_locator: 'Photon locator',
  platinum_journal: 'Platinum Journal',
  rainbow_ray_captured: 'Rainbow Ray inside of a Bio Transport Capsule',
  star_crabs: 'Star Crabs',
  translation_device: 'Translation device'
};

/**
 * Base inventory before container selection
 */
export const BASE_INVENTORY: InventoryItem[] = [
  { id: 'decompression_chamber', name: ITEM_NAMES.decompression_chamber, quantity: 1 },
  { id: 'hunting_permit', name: ITEM_NAMES.hunting_permit, quantity: 1 },
  { id: 'diving_fins', name: ITEM_NAMES.diving_fins, quantity: 7 },
  { id: 'diving_suits', name: ITEM_NAMES.diving_suits, quantity: 7 },
  { id: 'oxygen_tanks', name: ITEM_NAMES.oxygen_tanks, quantity: 7 },
  { id: 'capture_nets', name: ITEM_NAMES.capture_nets, quantity: 2 },
  { id: 'bio_capsule', name: ITEM_NAMES.bio_capsule, quantity: 1 },
  { id: 'fuel_canisters', name: ITEM_NAMES.fuel_canisters, quantity: 5 }
];

/**
 * Container items (Q4 selection)
 */
export const CONTAINER_ITEMS: Record<ContainerOption, InventoryItem[]> = {
  A: [
    { id: 'bio_capsule', name: ITEM_NAMES.bio_capsule, quantity: 1 },
    { id: 'environmental_cleanse', name: ITEM_NAMES.environmental_cleanse, quantity: 1 },
    { id: 'diving_bell_parts', name: ITEM_NAMES.diving_bell_parts, quantity: 1 },
    { id: 'extra_nets_fins', name: ITEM_NAMES.extra_nets_fins, quantity: 1 },
    { id: 'energy_meals', name: ITEM_NAMES.energy_meals, quantity: 2 }
  ],
  B: [
    { id: 'bio_capsule', name: ITEM_NAMES.bio_capsule, quantity: 1 },
    { id: 'emergency_decomp_suit', name: ITEM_NAMES.emergency_decomp_suit, quantity: 1 },
    { id: 'environmental_cleanse', name: ITEM_NAMES.environmental_cleanse, quantity: 1 },
    { id: 'extra_nets_fins', name: ITEM_NAMES.extra_nets_fins, quantity: 1 },
    { id: 'translation_device', name: ITEM_NAMES.translation_device, quantity: 1 }
  ],
  C: [
    { id: 'bio_capsule', name: ITEM_NAMES.bio_capsule, quantity: 1 },
    { id: 'emergency_decomp_suit', name: ITEM_NAMES.emergency_decomp_suit, quantity: 1 },
    { id: 'energy_meals', name: ITEM_NAMES.energy_meals, quantity: 4 },
    { id: 'photon_locator', name: ITEM_NAMES.photon_locator, quantity: 1 },
    { id: 'translation_device', name: ITEM_NAMES.translation_device, quantity: 1 }
  ],
  D: [] // None
};

/**
 * Add item to inventory
 */
export function addItem(inventory: InventoryItem[], itemId: ItemId, quantity: number = 1): InventoryItem[] {
  const existing = inventory.find(item => item.id === itemId);

  if (existing) {
    return inventory.map(item =>
      item.id === itemId
        ? { ...item, quantity: item.quantity + quantity }
        : item
    );
  }

  return [
    ...inventory,
    { id: itemId, name: ITEM_NAMES[itemId], quantity }
  ];
}

/**
 * Remove item from inventory
 */
export function removeItem(inventory: InventoryItem[], itemId: ItemId, quantity: number = 1): InventoryItem[] {
  return inventory
    .map(item =>
      item.id === itemId
        ? { ...item, quantity: Math.max(0, item.quantity - quantity) }
        : item
    )
    .filter(item => item.quantity > 0);
}

/**
 * Check if inventory has item
 */
export function hasItem(inventory: InventoryItem[], itemId: ItemId, minQuantity: number = 1): boolean {
  const item = inventory.find(i => i.id === itemId);
  return item ? item.quantity >= minQuantity : false;
}
